import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../../api/rpc'
import { IconLayers } from '../icons'
import { Button } from '../ui/Button'
import { Checkbox } from '../ui/Field'
import { Modal } from '../ui/Modal'
import { ModalFooter } from '../ui/DataTable'
import { useToast } from '../ui/Toast'

export interface KbReadable { id: string; name: string; description: string; workspace: string; workspaceId: string; documentCount: number }

export function useReadableCollections(): KbReadable[] {
  const [items, setItems] = useState<KbReadable[]>([])
  useEffect(() => { void rpc.call<{ items: KbReadable[] }>('kb.collections.readable').then(r => setItems(r.items)).catch(() => undefined) }, [])
  return items
}

/** Checkbox list of readable collections, grouped by workspace (shared by the chat picker and the automation editor). */
export function CollectionChecklist({ items, value, onChange }: { items: KbReadable[]; value: string[]; onChange(ids: string[]): void }) {
  const { t } = useTranslation()
  if (!items.length) return <p className="text-sm text-ink-500">{t('kb.noReadable')}</p>
  const groups = [...new Set(items.map(c => c.workspace))]
  return (
    <div className="space-y-3">
      {groups.map(g => (
        <div key={g} className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500">{g}</div>
          {items.filter(c => c.workspace === g).map(c => (
            <Checkbox key={c.id} checked={value.includes(c.id)} label={<span>{c.name}{c.description && <span className="text-ink-500"> — {c.description}</span>}</span>}
              onChange={e => onChange(e.target.checked ? [...value, c.id] : value.filter(x => x !== c.id))} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function KbScopePicker({ chatId, scope, disabled }: { chatId: string; scope: string[] | null | undefined; disabled?: boolean }) {
  const { t } = useTranslation()
  const toast = useToast()
  const items = useReadableCollections()
  const [draft, setDraft] = useState<string[] | null>(null)
  const count = scope?.length ?? 0
  const save = async () => {
    try { await rpc.call('chats.setKbScope', { chatId, collectionIds: draft?.length ? draft : null }); setDraft(null) } catch (err) { toast((err as Error).message, 'error') }
  }
  return (
    <>
      {/* On desktop (sm+), show icon + label + count/All. On mobile (<sm), show a compact
          icon-only button (with a badge dot when scoped) to fit in a single-row toolbar. */}
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => setDraft(scope ?? [])}
        title={t('kb.scopeTitle')}
        // The accessible name must contain the visible label (WCAG 2.5.3): on desktop the button
        // reads "Sources: …", so an aria-label of just the tooltip would hide what it says.
        aria-label={`${t('kb.scope')}: ${t('kb.scopeTitle')}`}
        className="relative h-7 min-w-0 max-sm:h-7 max-sm:w-7 max-sm:p-0 gap-1 px-1.5 text-xs text-ink-400 hover:bg-cream-100 hover:text-ink-100 sm:max-w-[11rem] shrink-0"
      >
        <IconLayers width={14} height={14} className="shrink-0 opacity-80" />
        <span className="hidden sm:inline text-ink-500">{t('kb.scope')}:</span>
        <span className="hidden sm:inline truncate">{count ? t('kb.scopeCount', { count }) : t('kb.scopeAll')}</span>
        {count > 0 && (
          <span
            className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-ginger-500 sm:hidden"
            aria-hidden="true"
          />
        )}
      </Button>
      <Modal open={draft !== null} title={t('kb.scopeTitle')} description={t('kb.scopeHint')} onClose={() => setDraft(null)}
        footer={<ModalFooter onCancel={() => setDraft(null)} onSubmit={() => void save()} />}>
        {draft && <CollectionChecklist items={items} value={draft} onChange={setDraft} />}
      </Modal>
    </>
  )
}
